/**
 * <Header />
 *
 * Glassmorphic site chrome at the top of every page. Adapts to auth state:
 *   * Signed-out -- shows Sign in / Join Parts Buoy CTAs.
 *   * Signed-in  -- shows username pill + a Sign out button.
 * Desktop has a search bar; on mobile we keep the bar lean and rely on
 * <BottomNav /> for primary navigation.
 */
import * as React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, Search, User2 } from "lucide-react";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import { useAuth } from "../../contexts/AuthContext";
import { useLogoContext } from "../../contexts/LogoContext";
import { cn } from "../../lib/cn";

function Wordmark() {
  const { logoUrl } = useLogoContext();
  return (
    <Link
      to="/"
      aria-label="Parts Buoy home"
      data-testid="header-logo"
      className="inline-flex items-center gap-3 rounded-xl bg-ink px-3 py-2 text-white hover:bg-ink-soft transition-colors"
    >
      <img
        src={logoUrl}
        alt=""
        aria-hidden
        className="h-9 w-9 md:h-10 md:w-10 object-contain"
        onError={(e) => { (e.currentTarget as HTMLImageElement).src = "/logo.png"; }}
      />
      <span className="font-display font-black tracking-[0.18em] text-base md:text-lg leading-none uppercase">
        Parts <span className="text-brand">Buoy</span>
      </span>
    </Link>
  );
}

const desktopLinks: Array<{ to: string; label: string }> = [
  { to: "/", label: "Marketplace" },
  { to: "/forums", label: "Forums" },
  { to: "/sell", label: "Sell" },
];

export function Header() {
  const [query, setQuery] = React.useState("");
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <header className="glass sticky top-0 z-40 w-full" data-testid="site-header">
      <div className="mx-auto max-w-7xl px-4 md:px-8 h-16 md:h-20 flex items-center gap-4">
        <Wordmark />

        <nav aria-label="Primary" className="hidden md:flex items-center gap-1 ml-4">
          {desktopLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`header-nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                cn(
                  "px-3 py-2 rounded-lg text-sm font-semibold transition-colors",
                  isActive ? "text-brand" : "text-ink hover:bg-surface-sunken",
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <form
          role="search"
          aria-label="Search parts"
          onSubmit={submitSearch}
          className="hidden md:flex flex-1 max-w-md ml-auto"
        >
          <label htmlFor="header-search" className="sr-only">
            Search parts
          </label>
          <div className="flex items-center w-full h-11 px-3 rounded-xl border-2 border-surface-border bg-white focus-within:border-ink">
            <Search className="h-5 w-5 text-ink-muted mr-2" aria-hidden />
            <input
              id="header-search"
              data-testid="header-search-input"
              type="text"
              placeholder="Search part numbers, models, keywords..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 bg-transparent outline-none placeholder:text-ink-subtle"
            />
          </div>
        </form>

        <div className="ml-auto md:ml-3 flex items-center gap-2">
          <Link
            to="/search"
            aria-label="Search"
            data-testid="header-mobile-search"
            className="md:hidden inline-flex items-center justify-center h-11 w-11 rounded-xl text-ink hover:bg-surface-sunken btn-target"
          >
            <Search className="h-6 w-6" aria-hidden />
          </Link>

          {user === undefined ? null : user ? (
            <>
              <Link
                to="/profile"
                data-testid="header-profile-link"
                className="hidden md:inline-flex items-center gap-2 rounded-xl border-2 border-surface-border px-3 h-11 font-semibold text-ink hover:border-ink btn-target"
              >
                <span>@{user.username}</span>
                {user.role === "DEALER" && <Badge variant="brand" size="sm">Dealer</Badge>}
                {user.role === "ADMIN" && <Badge variant="solid" size="sm">Admin</Badge>}
              </Link>
              <button
                type="button"
                onClick={doLogout}
                aria-label="Sign out"
                data-testid="header-logout"
                className="hidden md:inline-flex items-center justify-center h-11 w-11 rounded-xl text-ink hover:bg-surface-sunken btn-target"
              >
                <LogOut className="h-5 w-5" aria-hidden />
              </button>

              <Link
                to="/profile"
                aria-label="Profile"
                data-testid="header-mobile-profile"
                className="md:hidden inline-flex items-center justify-center h-11 w-11 rounded-xl text-ink hover:bg-surface-sunken btn-target"
              >
                <User2 className="h-6 w-6" aria-hidden />
              </Link>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-link" className="hidden md:inline-flex">
                <Button
                  variant="outline"
                  size="sm"
                  leadingIcon={<User2 className="h-5 w-5" />}
                >
                  Sign in
                </Button>
              </Link>
              <Link to="/register" data-testid="header-register-link" className="hidden md:inline-flex">
                <Button variant="primary" size="sm">
                  Join Parts Buoy
                </Button>
              </Link>
              <Link
                to="/login"
                aria-label="Sign in"
                data-testid="header-mobile-login"
                className="md:hidden inline-flex items-center justify-center h-11 w-11 rounded-xl text-ink hover:bg-surface-sunken btn-target"
              >
                <User2 className="h-6 w-6" aria-hidden />
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
